import { Alert, AlertTitle } from '@mui/material'
import React, { createContext, ReactNode, useEffect, useReducer } from 'react'

import { apiArticuloMoneda } from '../../modules/ArticuloMoneda/api/apiArticuloMoneda'
import { ArticuloMonedaProps } from '../../modules/ArticuloMoneda/interfaces'
import { apiKardexPeriodoActivo } from '../../modules/kardexPeriodo/api/apiKardexPeriodoActivo'
import { swalException } from '../../utils/swal.ts'
import { SimpleContainerBox } from '../components/Container/SimpleBox.tsx'
import MatxLoading from '../components/Template/MatxLoading/MatxLoading'
import { KardexPeriodoProps } from '../interfaces'

type StateProps = {
  isInitialised: boolean
  articuloMoneda: ArticuloMonedaProps
  kardexPeriodo: KardexPeriodoProps
}
const initialState: StateProps = {
  isInitialised: false,
  articuloMoneda: {} as ArticuloMonedaProps,
  kardexPeriodo: {} as KardexPeriodoProps,
}

const reducer = (state: StateProps, action: { type: string; payload: StateProps }) => {
  switch (action.type) {
    case 'INIT': {
      const { articuloMoneda, kardexPeriodo, isInitialised } = action.payload
      return {
        ...state,
        isInitialised,
        articuloMoneda,
        kardexPeriodo,
      }
    }
    case 'ARTICULO_MONEDA': {
      const { articuloMoneda, isInitialised } = action.payload

      return {
        ...state,
        isInitialised,
        articuloMoneda,
      }
    }
    case 'KARDEX_PERIODO': {
      const { kardexPeriodo, isInitialised } = action.payload

      return {
        ...state,
        isInitialised,
        kardexPeriodo,
      }
    }
    default: {
      return { ...state }
    }
  }
}

const OperacionesContext = createContext({
  ...initialState,
  refetchArticuloMoneda: () => Promise.resolve(),
  refetchKardexPeriodo: () => Promise.resolve(),
  refetchOperaciones: () => Promise.resolve(),
})

export interface OperacionesProviderProps {
  children: ReactNode
}

/**
 * DEfinimos las operaciones que se deben cumplir para iniciar operaciones relacionado a inventarios
 * @param children
 * @constructor
 */
export const OperacionesProvider = ({ children }: OperacionesProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  /**
   * Cargamos los datos
   */
  const loadOperaciones = async () => {
    const articuloMoneda = await apiArticuloMoneda().catch((e) => {
      console.log('error carga articulo moneda', e.message)
      return null
    })
    const kardexPeriodo = await apiKardexPeriodoActivo().catch((e) => {
      console.log('error carga kardex periodo', e.message)
      return null
    })

    return {
      articuloMoneda: (articuloMoneda || {}) as ArticuloMonedaProps,
      kardexPeriodo: (kardexPeriodo || {}) as KardexPeriodoProps,
      isInitialised: true,
    }
  }

  /**
   * Actualizamos ambos argumentos
   */
  const refetchOperaciones = async () => {
    const articuloMoneda = await apiArticuloMoneda().catch((e) => {
      console.log('error carga articulo moneda', e.message)
      return null
    })
    const kardexPeriodo = await apiKardexPeriodoActivo().catch((e) => {
      console.log('error carga kardex periodo', e.message)
      return null
    })
    dispatch({
      type: 'INIT',
      payload: {
        ...state,
        articuloMoneda: (articuloMoneda || {}) as ArticuloMonedaProps,
        kardexPeriodo: (kardexPeriodo || {}) as KardexPeriodoProps,
      },
    })
  }

  /**
   * Actualizamos articulo moneda
   */
  const refetchArticuloMoneda = async () => {
    const articuloMoneda = await apiArticuloMoneda().catch((e) => {
      console.log('error carga articulo moneda', e.message)
      return null
    })
    dispatch({
      type: 'ARTICULO_MONEDA',
      payload: {
        ...state,
        articuloMoneda: (articuloMoneda || {}) as ArticuloMonedaProps,
      },
    })
  }

  /**
   * Actualizamos el kardex periodo
   */
  const refetchKardexPeriodo = async () => {
    const kardexPeriodo = await apiKardexPeriodoActivo().catch((e) => {
      console.log('error carga articulo moneda', e.message)
      return null
    })
    dispatch({
      type: 'KARDEX_PERIODO',
      payload: {
        ...state,
        kardexPeriodo: (kardexPeriodo || {}) as KardexPeriodoProps,
      },
    })
  }

  useEffect(() => {
    ;(async () => {
      try {
        const { articuloMoneda, kardexPeriodo, isInitialised } = await loadOperaciones()
        dispatch({
          type: 'INIT',
          payload: {
            ...state,
            isInitialised,
            kardexPeriodo,
            articuloMoneda,
          },
        })
      } catch (error: any) {
        swalException(error)
        dispatch({
          type: 'INIT',
          payload: {
            ...state,
            isInitialised: true,
            kardexPeriodo: {} as KardexPeriodoProps,
            articuloMoneda: {} as ArticuloMonedaProps,
          },
        })
        console.error('Error al obtener los datos:', error.message)
      }
    })()
  }, [])

  /***********************************************************************************/
  /***********************************************************************************/
  /***********************************************************************************/
  /***********************************************************************************/
  /***********************************************************************************/

  if (!state.isInitialised) {
    return <MatxLoading />
  }
  if (!state.kardexPeriodo) {
    return (
      <SimpleContainerBox maxWidth={'md'}>
        <Alert severity={'warning'}>
          <AlertTitle>Alerta!</AlertTitle>
          Para acceder al contenido requiere que se realice el registro de{' '}
          <strong>PERIODO ACTIVO</strong>
        </Alert>
      </SimpleContainerBox>
    )
  }
  if (!state.articuloMoneda.monedaPrimaria) {
    return (
      <SimpleContainerBox maxWidth={'md'}>
        <Alert severity={'warning'}>
          <AlertTitle>Alerta!</AlertTitle>
          Para acceder al contenido requiere que se realice el registro de la{' '}
          <strong>MONEDA PRINCIPAL DE SISTEMA</strong>
        </Alert>
      </SimpleContainerBox>
    )
  }
  return (
    <OperacionesContext.Provider
      value={{
        ...state,
        refetchArticuloMoneda,
        refetchKardexPeriodo,
        refetchOperaciones,
      }}
    >
      {children}
    </OperacionesContext.Provider>
  )
}

export default OperacionesContext
